import { colors, cssThemedValue } from '@creditornot/cb-ingredients';
import React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { getSales, MenuItemsTotal } from 'apis/merchant-gateway/insights';
import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { formatPercentage } from 'modules/insights-v2/utils';

import { MenuTableColumnIDs, MenuTableSortableColumnIDs } from './types';
import SectionHeader from '../../SectionHeader';
import InfiniteTable from '../InfiniteTable';
import { SortOrder, SortState } from '../types';

type MenuItemsTotalItem = MenuItemsTotal & { id: string; currency: Currency };

export type MenuTableProps = {
  className?: string;
  isFetchingNextPage: boolean;
  isFetchingPreviousPage: boolean;
  items: MenuItemsTotalItem[];
  totalSales: number;
  totalUnits: number;
  sort?: SortState<MenuTableSortableColumnIDs>;
  isVATEnabled: boolean;
  handleSort?: (sort: SortState<MenuTableSortableColumnIDs>) => void;
  onFetchNextPage?: () => void;
  onFetchPreviousPage?: () => void;
};

const NAME_COLUMN_WIDTH = 30;
const SALES_COLUMN_WIDTH = 10;
const SALES_PERCENTAGE_COLUMN_WIDTH = 17;
const UNITS_COLUMN_WIDTH = 17;
const UNITS_PERCENTAGE_COLUMN_WIDTH = 17;
const ORDERS_COLUMN_WIDTH = 9;

const TrimmedText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Root = styled.div`
  border: 1px solid ${cssThemedValue({ light: colors.pepper8, dark: colors.salt12 })};
  border-radius: 8px;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  border-block-end: 1px solid ${cssThemedValue({ light: colors.pepper8, dark: colors.salt12 })};
  padding: 16px;
  display: flex;
`;

const MenuTable = ({
  className,
  items,
  isFetchingNextPage,
  isFetchingPreviousPage,
  totalUnits,
  totalSales,
  sort,
  isVATEnabled,
  onFetchNextPage,
  onFetchPreviousPage,
  handleSort,
}: MenuTableProps) => {
  const intl = useIntl();
  return (
    <Root>
      <Header>
        <SectionHeader title={intl.formatMessage({ id: 'insights.menu.table.title' })} />
      </Header>

      <InfiniteTable
        className={className}
        items={items}
        fetchingNextPage={isFetchingNextPage}
        fetchingPreviousPage={isFetchingPreviousPage}
        onFetchNextPage={onFetchNextPage}
        onFetchPreviousPage={onFetchPreviousPage}
        sort={sort}
        columns={[
          {
            id: MenuTableColumnIDs.NAME,
            label: intl.formatMessage({ id: 'insights.menu.table.name' }),
            width: NAME_COLUMN_WIDTH,
            render: (record: MenuItemsTotalItem) => (
              <TrimmedText title={record.itemName ?? undefined}>{record.itemName}</TrimmedText>
            ),
            enableSort: true,
            handleSort: (sortOrder: SortOrder) => handleSort?.({ sortBy: MenuTableColumnIDs.NAME, sortOrder }),
          },
          {
            id: MenuTableColumnIDs.SALES,
            label: intl.formatMessage({ id: 'insights.menu.table.sales' }),
            width: SALES_COLUMN_WIDTH,
            render: (record: MenuItemsTotalItem) => (
              <MonetaryAmount amount={getSales(record, isVATEnabled)} currency={record.currency} />
            ),
            enableSort: true,
            handleSort: (sortOrder: SortOrder) => handleSort?.({ sortBy: MenuTableColumnIDs.SALES, sortOrder }),
          },
          {
            id: MenuTableColumnIDs.SALES_PERCENTAGE,
            label: intl.formatMessage({ id: 'insights.menu.table.percentage-sales' }),
            width: SALES_PERCENTAGE_COLUMN_WIDTH,
            render: (record: MenuItemsTotalItem) =>
              `${formatPercentage(getSales(record, isVATEnabled) / totalSales, intl.locale)}`,
            enableSort: true,
            handleSort: (sortOrder: SortOrder) =>
              handleSort?.({ sortBy: MenuTableColumnIDs.SALES_PERCENTAGE, sortOrder }),
          },
          {
            id: MenuTableColumnIDs.UNITS,
            label: intl.formatMessage({ id: 'insights.menu.table.units' }),
            width: UNITS_COLUMN_WIDTH,
            render: (record: MenuItemsTotalItem) => record.units,
            enableSort: true,
            handleSort: (sortOrder: SortOrder) => handleSort?.({ sortBy: MenuTableColumnIDs.UNITS, sortOrder }),
          },
          {
            id: MenuTableColumnIDs.UNITS_PERCENTAGE,
            label: intl.formatMessage({ id: 'insights.menu.table.percentage-units' }),
            width: UNITS_PERCENTAGE_COLUMN_WIDTH,
            render: (record: MenuItemsTotalItem) => `${formatPercentage(record.units / totalUnits, intl.locale)}`,
            enableSort: true,
            handleSort: (sortOrder: SortOrder) =>
              handleSort?.({ sortBy: MenuTableColumnIDs.UNITS_PERCENTAGE, sortOrder }),
          },
          {
            id: MenuTableColumnIDs.ORDERS,
            label: intl.formatMessage({ id: 'insights.menu.table.orders' }),
            width: ORDERS_COLUMN_WIDTH,
            render: (record: MenuItemsTotalItem) => `${record.orders}`,
            enableSort: true,
            handleSort: (sortOrder: SortOrder) => handleSort?.({ sortBy: MenuTableColumnIDs.ORDERS, sortOrder }),
          },
        ]}
      />
    </Root>
  );
};
export default MenuTable;
